/**
 * Keeps the viewer's view state and the URL in step. Contract (decision
 * 2026-07-07, extended 2026-07-09 to FULL deep-link state): `?pack=` names
 * the pack and every other view coordinate is addressable too —
 *
 *   ?test=<id>              open test-instance detail
 *   ?step=<stepKey>         selected step inside that detail
 *   ?dock=network|console|terminal   open logs dock + stream
 *   ?tab=instances|issues   active run tab (report is the default → absent)
 *   ?issue=<test>:<ordinal> open RCA drawer for that failure
 *
 * Every unknown param is preserved untouched — the kaneai mount carries its
 * own (?evidenceId=…) on the same URL.
 *
 * Model: opening a detail from the list PUSHES a history entry, so the
 * browser Back button returns to the list; the in-app back button replays
 * that (history.back()) when we pushed, and strips the param in place when
 * the user deep-linked straight into a detail (going back would leave the
 * site). All OTHER view params (step/dock/tab/issue) mirror via
 * replaceState — no history spam while browsing — through one central
 * store subscription (syncViewParams), so no component needs to remember
 * to write the URL. The popstate listener derives view state from the URL —
 * the URL is the source of truth for which view is open.
 */

import { closeTestDetail, openIssueDrawer, openTestDetail, packStore, setDetailDrawer, setDetailLogsSub } from '../data/pack-store';
import { testIdFromSearch } from './resolve';
import { isInternalExplorerEnabled } from './internal';
/** True when the current detail's history entry was pushed by us (vs a
 * direct ?test= landing). Reset by popstate and by pack close. */
var pushed = false;
var hasHistory = function hasHistory() {
  return typeof window !== 'undefined' && typeof window.history !== 'undefined';
};

/** Open a test detail and reflect it in the URL (one pushed entry).
 * `preferredStepId` anchors the initial step selection (RCA drawer jump). */
export function openDetailWithUrl(id, preferredStepId) {
  if (hasHistory()) {
    var url = new URL(window.location.href);
    if (url.searchParams.get('test') !== id) {
      url.searchParams.set('test', id);
      window.history.pushState(window.history.state, '', url);
      pushed = true;
    }
  }
  openTestDetail(id, preferredStepId);
}

/** In-app back (button / Escape): undo our pushed entry when there is one,
 * otherwise strip the param in place. */
export function closeDetailWithUrl() {
  if (hasHistory() && pushed) {
    pushed = false;
    // popstate fires and closes the detail via the listener.
    window.history.back();
    return;
  }
  if (hasHistory()) {
    var url = new URL(window.location.href);
    if (url.searchParams.get('test') !== null) {
      url.searchParams["delete"]('test');
      window.history.replaceState(window.history.state, '', url);
    }
  }
  closeTestDetail();
}

/**
 * Close the detail IN PLACE (replaceState) — for lateral navigation (tab
 * bar, palette "Go to …") where the user's intent is a NEW view, not
 * "go back". closeDetailWithUrl's history.back() is async: its popstate
 * re-derives the previous entry's ?tab and would clobber the tab the
 * caller is about to set. Leaves the pre-push history entry behind, so a
 * later browser Back still returns to the older list view harmlessly.
 */
export function closeDetailInPlace() {
  pushed = false;
  if (hasHistory()) {
    var url = new URL(window.location.href);
    var owned = ['test', 'step', 'dock', 'attempt'];
    if (owned.some(function (p) {
      return url.searchParams.get(p) !== null;
    })) {
      owned.forEach(function (p) {
        return url.searchParams["delete"](p);
      });
      window.history.replaceState(window.history.state, '', url);
    }
  }
  closeTestDetail();
}

/** Drop a ?test= that names a test the open pack doesn't contain. */
export function stripTestParam() {
  if (!hasHistory()) return;
  var url = new URL(window.location.href);
  if (url.searchParams.get('test') !== null) {
    url.searchParams["delete"]('test');
    window.history.replaceState(window.history.state, '', url);
  }
}

/** Browser Back/Forward → derive the open view from the URL. Returns the
 * unlisten cleanup (the kaneai mount unmounts on route change). */
export function listenForTestParam(goTab) {
  if (typeof window === 'undefined') return function () {
    return undefined;
  };
  var onPop = function onPop() {
    var _link$tab;
    var link = deepLinkFromSearch(window.location.search);
    if (goTab) goTab((_link$tab = link.tab) !== null && _link$tab !== void 0 ? _link$tab : link.test ? 'instances' : 'report');
    if (link.test) {
      var _link$step, _link$attempt;
      openTestDetail(link.test, (_link$step = link.step) !== null && _link$step !== void 0 ? _link$step : undefined, (_link$attempt = link.attempt) !== null && _link$attempt !== void 0 ? _link$attempt : 0);
    } else {
      pushed = false;
      closeTestDetail();
    }
  };
  window.addEventListener('popstate', onPop);
  return function () {
    pushed = false;
    window.removeEventListener('popstate', onPop);
  };
}

// ─── Full view-state deep links (?tab / ?step / ?dock / ?issue) ───────────

// 'explorer' is the localStorage-gated internal surface — a deep link to
// it only resolves when the flag is on (everyone else falls back).
var TABS = ['report', 'instances', 'issues', 'coverage'];
var DOCKS = ['network', 'console', 'terminal', 'appium'];

/** Parse every view coordinate we understand out of a search string.
 * Unknown values are dropped (treated as absent), never thrown on. */
export function deepLinkFromSearch(search) {
  var p = new URLSearchParams(search);
  var tab = p.get('tab');
  var dock = p.get('dock');
  var attemptRaw = p.get('attempt');
  var attempt = attemptRaw !== null && /^[1-9]\d*$/.test(attemptRaw) ? parseInt(attemptRaw, 10) : null;
  return {
    tab: TABS.includes(tab) || tab === 'explorer' && isInternalExplorerEnabled() ? tab : null,
    test: testIdFromSearch(search),
    step: p.get('step'),
    attempt: attempt,
    dock: DOCKS.includes(dock) ? dock : null,
    issue: p.get('issue')
  };
}

/** Mutate the query string in place (replaceState) — never touches params
 * we don't own, never writes when nothing changed. */
function replaceParams(mutate) {
  if (!hasHistory()) return;
  var url = new URL(window.location.href);
  var before = url.searchParams.toString();
  mutate(url.searchParams);
  if (url.searchParams.toString() !== before) {
    window.history.replaceState(window.history.state, '', url);
  }
}

/** Reflect a landing-opened pack URL in ?pack= (replaceState), so the
 * view params written while browsing add up to a working deep link. */
export function writePackParam(packUrl) {
  replaceParams(function (p) {
    if (p.get('pack') !== packUrl) p.set('pack', packUrl);
  });
}

/** Reflect the active run tab. Report is the landing default, so it maps
 * to an ABSENT param — clean URLs for the common case. */
export function writeTabParam(tab) {
  replaceParams(function (p) {
    if (tab === 'report') p["delete"]('tab');else p.set('tab', tab);
  });
}

/** Stable URL identity for a failure row: "<testId>:<ordinal>" (falls back
 * to the culprit step id when the index has no ordinal). ULIDs never
 * contain ":", so the split is unambiguous. */
export function issueKeyOf(item) {
  var _ref, _item$ordinal;
  return "".concat(item.testId, ":").concat((_ref = (_item$ordinal = item.ordinal) !== null && _item$ordinal !== void 0 ? _item$ordinal : item.stepId) !== null && _ref !== void 0 ? _ref : '');
}

/** Find the attention row a ?issue= key names; null when the pack has no
 * such failure (bad or stale link). */
export function findAttentionItem(attention, key) {
  var _attention$items$find;
  if (!attention) return null;
  return (_attention$items$find = attention.items.find(function (it) {
    return issueKeyOf(it) === key;
  })) !== null && _attention$items$find !== void 0 ? _attention$items$find : null;
}

/** The dock coordinate of a detail's drawer, or null when closed. */
function dockOf(detail) {
  if (!detail || !detail.drawer.open) return null;
  return detail.drawer.tab === 'network' ? 'network' : detail.drawer.logsSub;
}

/** Apply a ?dock= value to the open detail's drawer. */
export function applyDockParam(dock) {
  if (dock === 'network') {
    setDetailDrawer(true, 'network');
  } else {
    setDetailDrawer(true, 'logs');
    setDetailLogsSub(dock);
  }
}

/**
 * The central store→URL mirror for step/dock/issue. One subscription for
 * the mount's lifetime instead of URL writes sprinkled through every
 * component — selectDetailStep, the dock buttons and the issue drawer all
 * stay URL-addressable without knowing the URL exists. replaceState only:
 * these are refinements of the current view, not navigation.
 */
export function syncViewParams() {
  if (!hasHistory()) return function () {
    return undefined;
  };
  return packStore.subscribe(function (s) {
    replaceParams(function (p) {
      var _s$detail$selectedSte, _s$detail, _s$detail$attempt, _s$detail2;
      var step = (_s$detail$selectedSte = (_s$detail = s.detail) === null || _s$detail === void 0 ? void 0 : _s$detail.selectedStepKey) !== null && _s$detail$selectedSte !== void 0 ? _s$detail$selectedSte : null;
      if (step) p.set('step', step);else p["delete"]('step');
      var dock = dockOf(s.detail);
      if (dock) p.set('dock', dock);else p["delete"]('dock');
      var attempt = (_s$detail$attempt = (_s$detail2 = s.detail) === null || _s$detail2 === void 0 ? void 0 : _s$detail2.attempt) !== null && _s$detail$attempt !== void 0 ? _s$detail$attempt : 0;
      if (attempt > 0) p.set('attempt', String(attempt));else p["delete"]('attempt');
      var issue = s.issueDrawer ? issueKeyOf(s.issueDrawer.item) : null;
      if (issue) p.set('issue', issue);else p["delete"]('issue');
    });
  });
}

/** Run `cb` once, as soon as `pick` yields a value (immediately when it
 * already does). Returns an unsubscribe for the not-yet-fired case. */
function once(pick, cb) {
  var now = pick(packStore.getState());
  if (now !== null && now !== undefined) {
    cb(now);
    return function () {
      return undefined;
    };
  }
  var un = packStore.subscribe(function (s) {
    var v = pick(s);
    if (v !== null && v !== undefined) {
      un();
      cb(v);
    }
  });
  return un;
}

/**
 * Replay a deep link against the (hydrating) pack once its data arrives:
 * ?test (+?step +?dock) opens the detail anchored on the step with the
 * dock restored; ?issue waits for the attention index and opens the RCA
 * drawer; ?tab lands the run view. Unknown ids strip their param — the
 * honest fallback is the view that exists. Returns a cleanup that cancels
 * any still-waiting subscriptions (pack closed / mount gone).
 */
export function applyDeepLink(goTab) {
  if (typeof window === 'undefined') return function () {
    return undefined;
  };
  var link = deepLinkFromSearch(window.location.search);
  var cleanups = [];
  if (link.tab) goTab(link.tab);
  if (link.test) {
    cleanups.push(once(
    // Wait for the row index to hydrate before judging the id.
    function (s) {
      return s.rowIds.length > 0 ? s.rowIds : null;
    }, function (rowIds) {
      var _link$step2, _link$attempt2;
      if (!rowIds.includes(link.test)) {
        stripTestParam();
        return;
      }
      goTab('instances');
      openTestDetail(link.test, (_link$step2 = link.step) !== null && _link$step2 !== void 0 ? _link$step2 : undefined, (_link$attempt2 = link.attempt) !== null && _link$attempt2 !== void 0 ? _link$attempt2 : 0);
      if (link.dock) {
        // The dock's lazy log loads need the detail's log index —
        // apply once the detail is ready, not while it streams.
        cleanups.push(once(function (s) {
          var _s$detail3;
          return ((_s$detail3 = s.detail) === null || _s$detail3 === void 0 ? void 0 : _s$detail3.phase) === 'ready' ? s.detail : null;
        }, function () {
          return applyDockParam(link.dock);
        }));
      }
    }));
  }

  // Independent of ?test since the drawer mounts globally: a link copied
  // with the drawer open OVER a detail replays both (drawer in place, no
  // tab flip); without ?test the drawer belongs to the Issues tab.
  if (link.issue) {
    cleanups.push(once(function (s) {
      return s.attention;
    }, function (attention) {
      var item = findAttentionItem(attention, link.issue);
      if (item) {
        if (!link.test) goTab('issues');
        openIssueDrawer(item);
      } else {
        replaceParams(function (p) {
          return p["delete"]('issue');
        });
      }
    }));
  }
  return function () {
    return cleanups.forEach(function (fn) {
      return fn();
    });
  };
}